import { useEffect, useRef, useState } from 'react';
import { Wind, Play, RotateCw, Check } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * RespRateCounter — tap-a-big-button-every-breath timer.
 *
 * Workflow (how rescue officers actually count RR in the field):
 *   1. Tap START — a 15-second countdown begins.
 *   2. For each observed breath, tap the big BREATH button. Counter
 *      increments.
 *   3. When the 15 s timer hits zero, the component auto-stops and
 *      multiplies the count × 4 for breaths-per-minute.
 *   4. Tap USE — the computed RR is written back to the vitals form
 *      via the onCommit callback. The form can then show the value
 *      and let the user keep it or overwrite it.
 *
 * Design goals:
 *   - The BREATH button is the single biggest touch target on screen
 *     while counting — a gloved thumb has to land reliably every
 *     time under adrenaline.
 *   - Progress bar shrinks from 100% → 0% so the officer can see
 *     how much time is left without looking at numbers.
 *   - 15 seconds was chosen over 30 or 60 because real counts in a
 *     back of an ambulance rarely get a full minute of stillness —
 *     pediatric abdominal breathers, agitated patients, etc.
 *     ×4 math is the trade-off for that speed.
 */

type Phase = 'idle' | 'counting' | 'done';

interface Props {
  /** Current RR stored in the parent form (if any). */
  current?: string;
  /** Fired when the user taps USE — integer breaths/min. */
  onCommit: (rr: number) => void;
  /** Optional label override (default: "Respiration Rate"). */
  label?: string;
}

const DURATION_MS = 15_000;

export function RespRateCounter({ current, onCommit, label = 'Respiration Rate' }: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [count, setCount] = useState(0);
  const [remainingMs, setRemainingMs] = useState(DURATION_MS);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  }, []);

  function startCount() {
    setPhase('counting');
    setCount(0);
    setRemainingMs(DURATION_MS);
    startedAtRef.current = Date.now();
    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = window.setInterval(() => {
      const elapsed = Date.now() - (startedAtRef.current ?? Date.now());
      const left = Math.max(0, DURATION_MS - elapsed);
      setRemainingMs(left);
      if (left <= 0) {
        if (tickRef.current) window.clearInterval(tickRef.current);
        tickRef.current = null;
        setPhase('done');
      }
    }, 50);
  }

  function registerBreath() {
    if (phase !== 'counting') return;
    setCount((c) => c + 1);
    // Small haptic nudge on devices that support it
    if ('vibrate' in navigator) try { navigator.vibrate?.(10); } catch { /* ignore */ }
  }

  function reset() {
    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = null;
    setPhase('idle');
    setCount(0);
    setRemainingMs(DURATION_MS);
  }

  function commitAndClose() {
    onCommit(count * 4);
    reset();
  }

  const rr = count * 4;
  const pct = (remainingMs / DURATION_MS) * 100;
  const seconds = (remainingMs / 1000).toFixed(1);

  return (
    <div className="border border-slate-700 bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-700 bg-slate-950 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
          <Wind size={12} className="text-blue-300" />
          {label} · 15-sec count
        </div>
        {current && phase === 'idle' && (
          <span className="text-[10px] font-mono text-slate-500">current: {current}</span>
        )}
      </div>

      {/* Body */}
      {phase === 'idle' && (
        <div className="p-2.5">
          <button
            type="button"
            onClick={startCount}
            className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white py-2 text-sm font-bold uppercase tracking-wider border border-blue-400"
          >
            <Play size={14} />
            Start 15-sec count
          </button>
          <p className="mt-1.5 text-[10px] text-slate-500 leading-snug">
            Tap START, then tap the big BREATH button once per observed breath. Count ends automatically at 0s and multiplies × 4 for breaths/min.
          </p>
        </div>
      )}

      {phase === 'counting' && (
        <div className="p-2 space-y-1.5">
          {/* Countdown + current count */}
          <div className="grid grid-cols-3 gap-1.5">
            <Stat label="Remaining" value={`${seconds}s`} accent="amber" />
            <Stat label="Breaths" value={String(count)} accent="blue" />
            <Stat label="Projected" value={`${count * 4}/min`} accent="emerald" />
          </div>
          {/* Progress bar */}
          <div className="h-1 w-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-amber-400 transition-[width] duration-75" style={{ width: `${pct}%` }} />
          </div>
          {/* THE big breath button */}
          <button
            type="button"
            onClick={registerBreath}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-4 text-2xl font-black uppercase tracking-widest',
              'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white',
              'border-2 border-blue-300 shadow-inner select-none',
            )}
          >
            <Wind size={22} />
            Breath
          </button>
          <button
            type="button"
            onClick={reset}
            className="w-full text-[11px] text-slate-400 hover:text-slate-200 py-1"
          >
            Cancel
          </button>
        </div>
      )}

      {phase === 'done' && (
        <div className="p-2.5 space-y-2">
          <div className="grid grid-cols-2 gap-1.5">
            <Stat label="Counted" value={`${count} breaths`} accent="blue" />
            <Stat label="Per Minute" value={`${rr}`} accent={rrAccent(rr)} big />
          </div>
          {/* Clinical context chip */}
          <div className={cn(
            'border px-2 py-1 text-[11px]',
            rrBannerClass(rr),
          )}>
            {rrInterpretation(rr)}
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={commitAndClose}
              className="flex-1 btn-primary gap-1 text-sm"
            >
              <Check size={14} />
              Use {rr}/min
            </button>
            <button
              type="button"
              onClick={startCount}
              className="btn-secondary gap-1 text-sm"
            >
              <RotateCw size={14} />
              Redo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label, value, accent, big,
}: {
  label: string; value: string; accent: 'amber' | 'blue' | 'emerald' | 'red'; big?: boolean;
}) {
  const bar = {
    amber:   'border-amber-700 bg-amber-950/30 text-amber-200',
    blue:    'border-blue-700  bg-blue-950/30  text-blue-200',
    emerald: 'border-emerald-700 bg-emerald-950/30 text-emerald-200',
    red:     'border-red-700  bg-red-950/30   text-red-200',
  }[accent];
  return (
    <div className={`border px-1.5 py-1 ${bar}`}>
      <div className="text-[9px] font-bold uppercase tracking-widest opacity-80">{label}</div>
      <div className={cn('font-black tabular-nums leading-none mt-0.5', big ? 'text-2xl' : 'text-base')}>{value}</div>
    </div>
  );
}

// Adult interpretation bands (NORMAL adult 12–20; see PediatricVitalsReference
// for age-bracketed bands that override this on pediatric patients).
function rrAccent(rr: number): 'amber' | 'blue' | 'emerald' | 'red' {
  if (rr === 0) return 'red';
  if (rr < 8) return 'red';
  if (rr < 12) return 'amber';
  if (rr <= 20) return 'emerald';
  if (rr <= 30) return 'amber';
  return 'red';
}
function rrBannerClass(rr: number): string {
  const a = rrAccent(rr);
  return {
    amber:   'border-amber-700 bg-amber-950/30 text-amber-200',
    blue:    'border-blue-700  bg-blue-950/30  text-blue-200',
    emerald: 'border-emerald-700 bg-emerald-950/30 text-emerald-200',
    red:     'border-red-700  bg-red-950/40   text-red-200',
  }[a];
}
function rrInterpretation(rr: number): string {
  if (rr === 0) return 'Apnea — bag-mask ventilate, secure airway.';
  if (rr < 8)  return `Bradypnea (${rr}/min) — hypoventilation risk. Assess ventilation quality + consider BVM support.`;
  if (rr < 12) return `Low-normal (${rr}/min) — monitor; acceptable in some quiet / sedated patients.`;
  if (rr <= 20) return `Normal adult range (${rr}/min).`;
  if (rr <= 30) return `Tachypnea (${rr}/min) — assess for distress / sepsis / acidosis / pain.`;
  return `Severe tachypnea (${rr}/min) — impending respiratory failure? Reassess.`;
}
